import { useEffect, useState } from "react";
import { apiBaseURL, studioApi } from "../api";
import {
  Space,
  Card,
  Table,
  Button,
  Badge,
  Image,
  Select,
  DatePicker,
  Checkbox,
  Input,
  Dropdown,
  MenuProps,
  Modal,
  message,
  Tag,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import { VideoCameraFilled, YoutubeFilled } from "@ant-design/icons";
import moment from "moment";
import { Link, useNavigate } from "@tanstack/react-router";
import { ISchedule, IVideo } from "../interfaces";
import useCourseStore from "../store/course";
import { ICourse } from "./list";
import AppHeader from "../layouts/header";

const INITIAL_FILTER = { course: 0, date: "", videoId: "", type: "" };
const UN_AUTHENTICATED_CODE = 401;

export default function Schedules() {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [youtubeLoadingId, setYoutubeLoadingId] = useState<number | null>(null);
  const [resData, setResData] = useState<ISchedule[]>([]);
  const [filteredCourses, setFilteredCourses] = useState<ICourse[]>([]);
  const { courses } = useCourseStore();
  const [schedules, setSchedules] = useState<ISchedule[]>([]);
  const [multiples, setMultiples] = useState<IVideo[]>([]);
  const [filter, setFilter] = useState(INITIAL_FILTER);
  const [youtubeUrl, setYoutubeUrl] = useState("");

  const navigate = useNavigate({ from: "/schedules" });

  const getSchedules = async () => {
    try {
      const Authorization = "Bearer " + localStorage.getItem("AUTH_TOKEN");
      const { data } = await apiBaseURL.get(`schedules`, {
        headers: { Authorization },
      });
      setLoading(false);
      setResData(data);
      setSchedules(data);
    } catch (e: any) {
      if (e?.response?.status === UN_AUTHENTICATED_CODE) {
        localStorage.removeItem("AUTH_TOKEN");
        navigate({ to: "/login" });
      }
    }
  };

  function filterSchedule(currentFilter: typeof INITIAL_FILTER) {
    return resData.filter((item: any) => {
      if (
        currentFilter.videoId &&
        !String(item.id)
          .toLowerCase()
          .includes(currentFilter.videoId.toLowerCase())
      ) {
        return false;
      }
      if (
        currentFilter.course !== 0 &&
        String(item?.course?.id) !== String(currentFilter.course)
      ) {
        return false;
      }
      if (currentFilter.date && item?.published_at) {
        const itemDate = moment(item.published_at).format("YYYY-MM-DD");
        const filterDate = moment(currentFilter.date).format("YYYY-MM-DD");

        if (itemDate !== filterDate) {
          return false;
        }
      }
      if (
        currentFilter.type &&
        String(item?.type).toLowerCase() !== currentFilter.type.toLowerCase()
      ) {
        return false;
      }
      return true;
    });
  }

  useEffect(() => {
    setSchedules(filterSchedule(filter));
  }, [filter, resData]);

  useEffect(() => {
    setFilteredCourses(courses);
  }, [courses]);

  useEffect(() => {
    getSchedules();
  }, []);

  const handleScheduleLive = async (record: any) => {
    const bucketId = localStorage.getItem("default_bucket");
    if (!bucketId) return message.error("Please select bucket ");
    const payload = {
      vid: record.id,
      videoType: "kgs",
      title: record.name,
      course_name: record.course.title,
      course_id: record.course.id,
      lesson_id: record.name,
      bucket: bucketId,
    };
    try {
      const { data } = await studioApi.post<IVideo>("videos", payload);
      navigate({ to: `/studio/${data._id}` });
    } catch (e: any) {
      console.log({ e });
    }
  };

  const handleYoutubeLive = async (record: any) => {
    try {
      setYoutubeLoadingId(record.id);
      const bucketId = localStorage.getItem("default_bucket");
      if (!bucketId) {
        return message.error("Please select bucket");
      }
      const youtubeId = localStorage.getItem("selected_youtube_channel");
      if (!youtubeId) {
        return message.error("Please select YouTube channel");
      }
      const payload = {
        vid: record.id,
        title: record.name,
        bucket: bucketId,
        course_id: record.course?.id,
        lesson_id: record.name,
      };
      const { data } = await studioApi.post(
        `/youtube/${youtubeId}/schedule`,
        payload
      );

      message.success("YouTube live scheduled successfully");

      navigate({
        to: `/studio/${data.data.video._id}`,
      });
    } catch (e: any) {
      console.log({ e });

      message.error(
        e?.response?.data?.message || "Failed to schedule YouTube live"
      );
    } finally {
      setYoutubeLoadingId(null);
    }
  };

  const columns: ColumnsType<ISchedule> = [
    {
      render: (_, record: any) => (
        <Checkbox
          checked={!!multiples.find((mul) => mul.vid === record.id)}
          onChange={(arg) => {
            const bucketId = localStorage.getItem("default_bucket");
            const selectedVideo: any = {
              key: record.key,
              vid: record.id,
              title: record.name,
              course_name: record.course.title,
              course_id: record.course.id,
              lesson_id: record.name,
              bucket: bucketId,
            };
            const checked = arg.target.checked;
            if (checked === true) {
              return setMultiples((multiples) => [...multiples, selectedVideo]);
            }
            return setMultiples((multiples) =>
              multiples.filter((item: any) => item.vid !== selectedVideo.vid)
            );
          }}
        ></Checkbox>
      ),
    },
    {
      title: "S No",
      dataIndex: "index",
      key: "index",
      render: (a, b, index) => <p>{index + 1}</p>,
      responsive: ["lg", "md"],
    },
    {
      title: "VID",
      dataIndex: "id",
      key: "id",
      align: "center",
      responsive: ["lg", "md"],
    },
    {
      title: <div style={{ textAlign: "center" }}>Topic</div>,
      dataIndex: "name",
      key: "name",
      render: (_, record) => (
        <Space size="middle">
          <Image
            src={record.thumb || "./default_image.jpg"}
            height="40px"
            width="auto"
          />
          <p style={{ textAlign: "center" }}>{record.name}</p>
        </Space>
      ),
    },
    {
      title: "Course",
      dataIndex: "course",
      key: "course",
      render: (course) => <a>{course?.title}</a>,
      align: "center",
      responsive: ["lg"],
    },

    {
      title: "Type",
      dataIndex: "type",
      key: "type",
      align: "center",
      responsive: ["lg", "md"],
      render: (type: string) => {
        const typeConfig: Record<string, { label: string; color: string }> = {
          yt: {
            label: "YouTube",
            color: "red",
          },
          public: {
            label: "Public",
            color: "green",
          },
          kgs: {
            label: "KGS",
            color: "blue",
          },
        };

        const config = typeConfig[type?.toLowerCase()] ?? {
          label: type || "Unknown",
          color: "default",
        };

        return (
          <Tag
            color={config.color}
            style={{
              borderRadius: 999,
              padding: "2px 10px",
              fontWeight: 600,
              margin: 0,
            }}
          >
            {config.label}
          </Tag>
        );
      },
    },
    {
      title: "Publish At",
      dataIndex: "published_at",
      key: "published_at",
      render: (published_at) => (
        <Badge>{moment(published_at).format("DD-MM-YY HH:mm")}</Badge>
      ),
      align: "center",
      responsive: ["lg", "md"],
    },
    {
      title: "Action",
      key: "action",
      render: (_, record) => (
        <Space size="middle">
          <Button onClick={() => handleScheduleLive(record)}>
            <VideoCameraFilled />
          </Button>

          <Button
            danger
            loading={youtubeLoadingId === record.id}
            onClick={() => handleYoutubeLive(record)}
          >
            <YoutubeFilled
              style={{
                color: "#ff0000",
              }}
            />
          </Button>
        </Space>
      ),
      align: "center",
    },
  ];

  const handleBulkLive = async () => {
    try {
      await studioApi.post("videos/bulk", {
        data: multiples,
      });
      navigate({ to: "/list" });
    } catch (error: any) {
      message.error(error.message || "something went wrong");
    }
  };

  const items: MenuProps["items"] = [
    {
      key: "1",
      label: <Button onClick={handleBulkLive}>Live Via Studio</Button>,
    },
    {
      key: "2",
      label: <Button onClick={() => setOpen(true)}>Live Via Youtube</Button>,
    },
  ];

  async function handleYoutubeSchedule() {
    try {
      const payload = { data: multiples, url: youtubeUrl };
      await studioApi.post("videos/youtube", payload);
      setOpen(true);
      navigate({ to: "/list" });
    } catch (error: any) {
      message.error(error.message || "something went wrong");
    }
  }

  return (
    <>
      <AppHeader />
      <Card title="Schedules Classes">
        <Space
          style={{
            display: "flex",
            gap: "10px",
            alignItems: "center",
            padding: "20px",
            background: "rgba(0,0,0,0.02)",
            borderRadius: "0.5em",
            marginBottom: "1em",
            overflowX: "auto",
            width: "100%",
            justifyContent: "space-between",
          }}
        >
          <div style={{ display: "flex", gap: "10px" }}>
            {multiples.length > 0 && (
              <Dropdown menu={{ items }} placement="bottomLeft">
                <Button>
                  <VideoCameraFilled />
                  Bulk Go Live
                </Button>
              </Dropdown>
            )}
            <Input
              onChange={({ target }) =>
                setFilter((f) => ({ ...f, videoId: target.value }))
              }
              placeholder="Enter video id to Search"
              style={{ width: 220 }}
            />
            <Select
              showSearch
              allowClear
              value={filter.course || undefined}
              onClear={() => setFilter((f) => ({ ...f, course: 0 }))}
              placeholder="Select the course"
              style={{ width: 220 }}
              options={filteredCourses.map((course) => ({
                label: `${course.id} - ${course.title}`,
                value: course.id,
              }))}
              onChange={(value) =>
                setFilter((f) => ({ ...f, course: value ?? 0 }))
              }
              filterOption={(input, option) =>
                option?.label
                  ?.toString()
                  .toLowerCase()
                  .includes(input.toLowerCase()) ?? false
              }
            />
            <Select
              allowClear
              value={filter.type || undefined}
              placeholder="Select type"
              style={{ width: 180 }}
              options={[
                { label: "YouTube", value: "yt" },
                { label: "Public", value: "public" },
                { label: "KGS", value: "kgs" },
              ]}
              onChange={(value) =>
                setFilter((f) => ({ ...f, type: value ?? "" }))
              }
            />
            <DatePicker
              onChange={(_, dateString: any) =>
                setFilter((f) => ({ ...f, date: dateString }))
              }
              picker="date"
              style={{ width: 220 }}
            />
          </div>
          <Link to="/go-live" from="/">
            <Button danger={true}>
              <VideoCameraFilled />
              Schedule Live
            </Button>
          </Link>
        </Space>

        <Table
          columns={columns}
          dataSource={schedules}
          loading={loading}
          scroll={{ x: 800 }}
          pagination={{
            position: ["bottomCenter"],
          }}
        />
      </Card>
      <Modal
        open={open}
        title="Youtube Url"
        onOk={handleYoutubeSchedule}
        onCancel={() => setOpen(false)}
        footer={(_, { OkBtn, CancelBtn }) => (
          <>
            <CancelBtn />
            <OkBtn />
          </>
        )}
      >
        <Input
          placeholder="Enter Youtube link"
          onChange={({ target }) => setYoutubeUrl(target.value)}
        />
      </Modal>
    </>
  );
}
